import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alertApi } from '../services/api';
import { useRealtimeStore } from '../store/realtimeStore';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import clsx from 'clsx';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  BellAlertIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

export default function AlertsPage() {
  const queryClient = useQueryClient();
  const { alerts: realtimeAlerts } = useRealtimeStore();
  const { getEffectiveUnitIds, isHQ } = useUnitStore();
  const { user } = useAuthStore();
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [showResolved, setShowResolved] = useState(false);

  // Get effective unit IDs for filtering
  // For unit users: use their unitId (locked to their unit)
  // For Brigade HQ: use selected units or all
  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
  const userIsHQ = isHQ(user?.unitId || null);
  // For unit users: always pass their unitId to API
  // For Brigade HQ (including 86ISB): pass unitId only if a specific unit is selected
  const unitIdForApi = userIsHQ
    ? (effectiveUnitIds && effectiveUnitIds.length === 1 ? effectiveUnitIds[0] : undefined)
    : (user?.unitId || undefined);

  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['alerts', severityFilter, showResolved, effectiveUnitIds],
    queryFn: () => alertApi.getAll({
      severity: severityFilter || undefined,
      activeOnly: !showResolved,
      unitId: unitIdForApi,
    }),
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  const { data: statsData } = useQuery({
    queryKey: ['alert-stats', effectiveUnitIds],
    queryFn: () => alertApi.getStats({ unitId: unitIdForApi }),
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => alertApi.acknowledge(id),
    onSuccess: () => {
      toast.success('Alert acknowledged');
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => alertApi.resolve(id),
    onSuccess: () => {
      toast.success('Alert resolved');
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const acknowledgeAllMutation = useMutation({
    mutationFn: (severity?: string) => alertApi.acknowledgeAll(severity, unitIdForApi),
    onSuccess: (_, severity) => {
      toast.success(`All ${severity || ''} alerts acknowledged`);
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  // Filter realtime alerts by unit if needed
  const filteredRealtimeAlerts = useMemo(() => {
    if (!effectiveUnitIds) return realtimeAlerts;
    return realtimeAlerts.filter((alert: any) => 
      effectiveUnitIds.includes(alert.unitId || '')
    );
  }, [realtimeAlerts, effectiveUnitIds]);

  // Use API data if available, otherwise use filtered realtime alerts
  const alerts = alertsData?.data?.alerts || filteredRealtimeAlerts;
  const stats = statsData?.data;

  const severityColors: Record<string, string> = {
    CRITICAL: 'bg-red-500/20 text-red-400 border-red-500/30',
    HIGH: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    LOW: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    INFO: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-display font-bold text-gray-100">Alerts</h1>
          <p className="text-gray-500 text-sm">System alerts and notifications</p>
        </div>
        <button
          onClick={() => acknowledgeAllMutation.mutate(undefined)}
          disabled={acknowledgeAllMutation.isPending}
          className="px-4 py-2 min-h-11 bg-command-accent text-command-bg rounded-lg font-semibold hover:bg-command-accent/90 transition-colors disabled:opacity-50 touch-manipulation"
        >
          Acknowledge All
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map((severity) => (
            <div 
              key={severity}
              className={clsx(
                'p-4 rounded-xl border',
                severityColors[severity]
              )}
            >
              <p className="text-sm opacity-80">{severity}</p>
              <p className="text-2xl font-bold">
                {stats.active?.bySeverity?.[severity] || 0}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map((severity) => (
            <button
              key={severity}
              onClick={() => setSeverityFilter(severity)}
              className={clsx(
                'px-3 py-1.5 min-h-11 lg:min-h-0 rounded text-sm transition-colors touch-manipulation',
                severityFilter === severity
                  ? 'bg-command-accent text-command-bg'
                  : 'bg-command-panel border border-command-border text-gray-400'
              )}
            >
              {severity || 'All'}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 min-h-11 text-sm text-gray-400 cursor-pointer touch-manipulation">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="rounded border-gray-600 w-5 h-5 lg:w-4 lg:h-4"
          />
          Show resolved
        </label>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {alerts.map((alert: any) => (
          <div
            key={alert.id}
            className={clsx(
              'p-4 rounded-xl border transition-colors',
              alert.status === 'ACTIVE' 
                ? severityColors[alert.severity]
                : 'bg-command-panel border-command-border opacity-60'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={clsx(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  severityColors[alert.severity]
                )}>
                  {alert.severity === 'CRITICAL' ? (
                    <ExclamationCircleIcon className="w-6 h-6" />
                  ) : (
                    <BellAlertIcon className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-100">{alert.title}</p>
                  <p className="text-sm text-gray-400">{alert.message}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{alert.type}</span>
                    <span>{format(new Date(alert.createdAt), 'HH:mm:ss dd/MM')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={clsx(
                  'px-2 py-1 rounded text-xs font-semibold',
                  alert.status === 'ACTIVE' && 'bg-red-500/20 text-red-400',
                  alert.status === 'ACKNOWLEDGED' && 'bg-yellow-500/20 text-yellow-400',
                  alert.status === 'RESOLVED' && 'bg-green-500/20 text-green-400'
                )}>
                  {alert.status}
                </span>
              </div>
            </div>

            {alert.status === 'ACTIVE' && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-current/20">
                <button
                  onClick={() => acknowledgeMutation.mutate(alert.id)}
                  disabled={acknowledgeMutation.isPending}
                  className="flex-1 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => resolveMutation.mutate(alert.id)}
                  disabled={resolveMutation.isPending}
                  className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  Resolve
                </button>
              </div>
            )}
          </div>
        ))}

        {!isLoading && alerts.length === 0 && (
          <div className="text-center py-12">
            <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-gray-400">No alerts</p>
          </div>
        )}
      </div>
    </div>
  );
}

